import { useState } from 'react'
import { Copy, Check, Wand2, AlertTriangle } from 'lucide-react'

// ─── Transformation logic ────────────────────────────────────────────────────

const MONITORING_URL = 'https://numberskills.azure-api.net/monitoring/v2/events'

const MONITORING_KEY_HEADER = {
  value: '@pipeline().parameters.MonitoringKey',
  type: 'Expression',
}

const WEB_ACTIVITY_POLICY = {
  timeout: '0.00:00:30',
  retry: 1,
  retryIntervalInSeconds: 30,
  secureInput: true,
  secureOutput: false,
}

function webActivityHeaders() {
  return {
    'Content-Type': 'application/json',
    'Ocp-Apim-Subscription-Key': MONITORING_KEY_HEADER,
  }
}

function buildAndChain(conditions) {
  if (conditions.length === 1) return conditions[0]
  return `and(${conditions[0]},${buildAndChain(conditions.slice(1))})`
}

function buildStartBody() {
  return (
    "@concat('{\"event_type\":\"pipeline_run\",\"status\":\"running\"," +
    "\"pipeline_name\":\"',pipeline().Pipeline,'\"," +
    "\"workspace_name\":\"',pipeline().parameters.WorkspaceName,'\"," +
    "\"pipeline_run_id\":\"',pipeline().RunId,'\"," +
    "\"duration_seconds\":0}')"
  )
}

function buildEndBody(leafNames) {
  const allSucceeded = buildAndChain(
    leafNames.map(n => `equals(activity('${n}').Status,'Succeeded')`)
  )

  // Error message: first failed leaf's message (empty when all succeeded)
  const errorExpr =
    leafNames.length === 1
      ? `if(equals(activity('${leafNames[0]}').Status,'Succeeded'),'',` +
        `replace(string(activity('${leafNames[0]}').Error.Message),'\"','\\\"'))`
      : leafNames
          .map(
            n =>
              `if(equals(activity('${n}').Status,'Succeeded'),'',` +
              `concat('[${n}] ',replace(string(activity('${n}').Error.Message),'\"','\\\"')))`
          )
          .reduce((a, b) => `if(not(empty(${a})),${a},${b})`)

  return (
    `@concat('{\"event_type\":\"pipeline_run\",\"status\":\"',` +
    `if(${allSucceeded},'success','failed'),'\"` +
    `,\"pipeline_name\":\"',pipeline().Pipeline,'\"` +
    `,\"workspace_name\":\"',pipeline().parameters.WorkspaceName,'\"` +
    `,\"pipeline_run_id\":\"',pipeline().RunId,'\"` +
    `,\"duration_seconds\":',string(div(sub(ticks(utcNow()),ticks(pipeline().TriggerTime)),10000000))` +
    `,',"error_message\":\"',${errorExpr},'\"}')`
  )
}

function makeMonitorStart() {
  return {
    name: 'Monitor_Start',
    description: 'Send pipeline start event to Numberskills Monitoring. Set Continue on error = true.',
    type: 'WebActivity',
    dependsOn: [],
    policy: WEB_ACTIVITY_POLICY,
    typeProperties: {
      method: 'POST',
      url: MONITORING_URL,
      headers: webActivityHeaders(),
      body: { value: buildStartBody(), type: 'Expression' },
    },
  }
}

function makeMonitorEnd(leafNames) {
  return {
    name: 'Monitor_End',
    description: 'Send pipeline completion event to Numberskills Monitoring.',
    type: 'WebActivity',
    dependsOn: leafNames.map(name => ({
      activity: name,
      dependencyConditions: ['Succeeded', 'Failed', 'Skipped'],
    })),
    policy: WEB_ACTIVITY_POLICY,
    typeProperties: {
      method: 'POST',
      url: MONITORING_URL,
      headers: webActivityHeaders(),
      body: { value: buildEndBody(leafNames), type: 'Expression' },
    },
  }
}

function injectMonitoring(jsonText) {
  let pipeline
  try {
    pipeline = JSON.parse(jsonText)
  } catch {
    throw new Error('Invalid JSON — check for syntax errors and try again.')
  }

  const activities = pipeline?.properties?.activities
  if (!Array.isArray(activities)) {
    throw new Error('Not a valid Fabric pipeline — missing properties.activities array.')
  }

  for (const reserved of ['Monitor_Start', 'Monitor_End']) {
    if (activities.find(a => a.name === reserved)) {
      throw new Error(
        `Pipeline already contains '${reserved}'. Remove it before injecting monitoring.`
      )
    }
  }

  // Root activities: nothing they depend on (or empty dependsOn)
  const rootNames = new Set(
    activities.filter(a => !a.dependsOn?.length).map(a => a.name)
  )

  // Leaf activities: no other activity lists them as a dependency
  const dependedOn = new Set(
    activities.flatMap(a => (a.dependsOn ?? []).map(d => d.activity))
  )
  const leafNames = activities.filter(a => !dependedOn.has(a.name)).map(a => a.name)

  if (leafNames.length === 0) {
    throw new Error('Could not detect any leaf activities — the pipeline may have a dependency cycle.')
  }

  // Wire root activities to depend on Monitor_Start
  const updatedActivities = activities.map(a => {
    if (!rootNames.has(a.name)) return a
    return {
      ...a,
      dependsOn: [
        { activity: 'Monitor_Start', dependencyConditions: ['Succeeded'] },
        ...(a.dependsOn ?? []),
      ],
    }
  })

  return {
    ...pipeline,
    properties: {
      ...pipeline.properties,
      activities: [makeMonitorStart(), ...updatedActivities, makeMonitorEnd(leafNames)],
      parameters: {
        MonitoringKey: { type: 'string', defaultValue: 'REPLACE_WITH_YOUR_API_KEY' },
        WorkspaceName: { type: 'string', defaultValue: 'REPLACE_WITH_YOUR_WORKSPACE_NAME' },
        ...(pipeline.properties.parameters ?? {}),
      },
    },
  }
}

// ─── UI ──────────────────────────────────────────────────────────────────────

export default function PipelineInjector() {
  const [input, setInput] = useState('')
  const [output, setOutput] = useState('')
  const [error, setError] = useState(null)
  const [copied, setCopied] = useState(false)
  const [info, setInfo] = useState(null)

  const handleInject = () => {
    setError(null)
    setOutput('')
    setInfo(null)
    try {
      const result = injectMonitoring(input)
      const activities = result.properties.activities
      const leafCount = result.properties.activities
        .find(a => a.name === 'Monitor_End')?.dependsOn?.length ?? 0
      setOutput(JSON.stringify(result, null, 2))
      setInfo({
        total: activities.length,
        leaves: leafCount,
      })
    } catch (e) {
      setError(e.message)
    }
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(output).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  const handleClear = () => {
    setInput('')
    setOutput('')
    setError(null)
    setInfo(null)
  }

  return (
    <div className="dashboard">
      <div className="page-header">
        <h1>Pipeline Monitoring Injector</h1>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '0.6rem 1rem', marginBottom: '1.25rem', background: 'rgba(237, 137, 54, 0.1)', border: '1px solid var(--warning)', borderRadius: 'var(--border-radius)', color: 'var(--warning)', fontSize: '0.85rem', fontWeight: 500 }}>
        ⚠ Work in progress — output should be reviewed before use in production pipelines.
      </div>

      <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem', maxWidth: 640 }}>
        Paste a bare Fabric pipeline JSON. The tool will wrap it with{' '}
        <strong>Monitor_Start</strong>, wire all leaf activities to{' '}
        <strong>Monitor_End</strong>, and add the monitoring parameters — entirely
        client-side, nothing is sent to any server.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', alignItems: 'start' }}>
        {/* Input */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <label style={{ fontWeight: 600, fontSize: '0.875rem' }}>Input pipeline JSON</label>
            <button
              className="btn-secondary"
              style={{ fontSize: '0.75rem', padding: '3px 10px' }}
              onClick={handleClear}
            >
              Clear
            </button>
          </div>
          <textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder='Paste your Fabric pipeline JSON here…'
            style={{
              width: '100%',
              height: 480,
              fontFamily: 'monospace',
              fontSize: '0.8rem',
              padding: '0.75rem',
              border: '1px solid var(--border)',
              borderRadius: 'var(--border-radius)',
              background: 'var(--bg-input)',
              color: 'var(--text-primary)',
              resize: 'vertical',
            }}
          />
          <button
            className="btn-primary"
            onClick={handleInject}
            disabled={!input.trim()}
            style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <Wand2 size={15} />
            Inject monitoring
          </button>
        </div>

        {/* Output */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <label style={{ fontWeight: 600, fontSize: '0.875rem' }}>Output pipeline JSON</label>
            {output && (
              <button
                className="btn-secondary"
                style={{ fontSize: '0.75rem', padding: '3px 10px', display: 'flex', alignItems: 'center', gap: '5px' }}
                onClick={handleCopy}
              >
                {copied ? <Check size={13} /> : <Copy size={13} />}
                {copied ? 'Copied!' : 'Copy'}
              </button>
            )}
          </div>

          {error && (
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px',
              padding: '0.75rem 1rem',
              borderLeft: '3px solid var(--danger)',
              background: 'rgba(245, 101, 101, 0.08)',
              borderRadius: '4px',
              marginBottom: '0.75rem',
              color: 'var(--danger)',
              fontSize: '0.875rem',
            }}>
              <AlertTriangle size={15} style={{ marginTop: 2, flexShrink: 0 }} />
              {error}
            </div>
          )}

          {info && (
            <div style={{
              padding: '0.5rem 0.75rem',
              background: 'var(--bg-card-alt)',
              border: '1px solid var(--border)',
              borderRadius: '4px',
              marginBottom: '0.75rem',
              fontSize: '0.8rem',
              color: 'var(--text-secondary)',
              display: 'flex',
              gap: '1.5rem',
            }}>
              <span>✓ Monitoring injected</span>
              <span>{info.total} total activities</span>
              <span>Monitor_End watches {info.leaves} leaf {info.leaves === 1 ? 'activity' : 'activities'}</span>
            </div>
          )}

          <textarea
            value={output}
            readOnly
            placeholder='Transformed pipeline will appear here…'
            style={{
              width: '100%',
              height: 480,
              fontFamily: 'monospace',
              fontSize: '0.8rem',
              padding: '0.75rem',
              border: '1px solid var(--border)',
              borderRadius: 'var(--border-radius)',
              background: 'var(--bg-card-alt)',
              color: 'var(--text-primary)',
              resize: 'vertical',
            }}
          />
        </div>
      </div>

      <div style={{ marginTop: '1.5rem', padding: '1rem', background: 'var(--bg-card-alt)', borderRadius: 'var(--border-radius)', border: '1px solid var(--border)', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
        <strong style={{ color: 'var(--text-primary)' }}>After injecting:</strong> update{' '}
        <code>MonitoringKey</code> and <code>WorkspaceName</code> parameter defaults before importing
        into Fabric. The injector sets them to placeholder strings as a reminder.
      </div>
    </div>
  )
}
